import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { BarChart3, PieChart, Users, FileText, Lightbulb, TrendingUp } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useAdminStats } from '@/features/admin/hooks/use-admin-stats'
import { useReportsByDay, usePatientsDistribution } from '@/features/admin/hooks/use-admin-reports'
import { ReportsChart } from '@/features/admin/components/ReportsChart'
import { PatientsDistribution } from '@/features/admin/components/PatientsDistribution'

export const Route = createFileRoute('/_authenticated/admin/analytics')({
  component: AdminAnalyticsPage,
})

function AdminAnalyticsPage() {
  useDocumentTitle('Analíticos')
  const [period, setPeriod] = useState('30')
  const [activeTab, setActiveTab] = useState('overview')

  const { data: stats, isLoading: statsLoading } = useAdminStats()
  const { data: reportsByDay, isLoading: reportsLoading } = useReportsByDay(parseInt(period))
  const { data: patientsDistribution, isLoading: distributionLoading } = usePatientsDistribution()

  return (
    <MainLayout title="Analytics" subtitle="Métricas e análises da organização">
      <div className="space-y-6">
        {/* Tabs + filtro de período */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="card p-1 inline-flex gap-1 w-full sm:w-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'overview'
                  ? 'bg-teal-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              Visão geral
            </button>
            <button
              onClick={() => setActiveTab('patients')}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'patients'
                  ? 'bg-teal-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <PieChart className="h-4 w-4" />
              Pacientes
            </button>
          </div>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="7">Últimos 7 dias</option>
            <option value="30">Últimos 30 dias</option>
            <option value="90">Últimos 90 dias</option>
          </select>
        </div>

        {/* Conteúdo das tabs */}
        {activeTab === 'overview' ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <SummaryCard
                title="Pacientes Ativos"
                value={stats?.activePatients ?? 0}
                total={stats?.totalPatients ?? 0}
                icon={Users}
                color="blue"
                isLoading={statsLoading}
              />
              <SummaryCard
                title="Relatos no Periodo"
                value={stats?.reportsThisMonth ?? 0}
                total={stats?.totalReports ?? 0}
                icon={FileText}
                color="teal"
                isLoading={statsLoading}
              />
              <SummaryCard
                title="Alertas Pendentes"
                value={stats?.pendingInsights ?? 0}
                total={stats?.totalInsights ?? 0}
                icon={Lightbulb}
                color="amber"
                isLoading={statsLoading}
              />
              <SummaryCard
                title="Profissionais Ativos"
                value={stats?.activeProfessionals ?? 0}
                total={stats?.totalProfessionals ?? 0}
                icon={TrendingUp}
                color="purple"
                isLoading={statsLoading}
              />
            </div>

            {/* Reports Chart */}
            <div className="card p-6">
              <ReportsChart data={reportsByDay} isLoading={reportsLoading} />
            </div>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <div className="card p-6">
              <PatientsDistribution
                data={patientsDistribution}
                isLoading={distributionLoading}
              />
            </div>

            <div className="card p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Metricas de Pacientes</h3>
              {statsLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                <div className="space-y-3">
                  <MetricRow
                    label="Total de pacientes"
                    value={stats?.totalPatients ?? 0}
                  />
                  <MetricRow
                    label="Pacientes ativos"
                    value={stats?.activePatients ?? 0}
                    percentage={
                      stats?.totalPatients
                        ? Math.round(((stats.activePatients ?? 0) / stats.totalPatients) * 100)
                        : 0
                    }
                  />
                  <MetricRow
                    label="Media de relatos por paciente"
                    value={
                      stats?.activePatients
                        ? Math.round((stats.totalReports ?? 0) / stats.activePatients)
                        : 0
                    }
                  />
                  <MetricRow
                    label="Media de alertas por paciente"
                    value={
                      stats?.activePatients
                        ? Math.round((stats.totalInsights ?? 0) / stats.activePatients)
                        : 0
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  )
}

const colorVariants = {
  blue: 'bg-blue-50 text-blue-600',
  teal: 'bg-teal-50 text-teal-600',
  amber: 'bg-amber-50 text-amber-600',
  purple: 'bg-purple-50 text-purple-600',
}

function SummaryCard({
  title,
  value,
  total,
  icon: Icon,
  color = 'teal',
  isLoading,
}: {
  title: string
  value: number
  total: number
  icon: React.ElementType
  color?: 'blue' | 'teal' | 'amber' | 'purple'
  isLoading?: boolean
}) {
  if (isLoading) {
    return (
      <div className="card p-6">
        <Skeleton className="h-4 w-24 mb-2" />
        <Skeleton className="h-8 w-16" />
      </div>
    )
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-semibold text-gray-900 font-poppins">{value.toLocaleString()}</p>
          <p className="text-xs text-gray-400">
            de {total.toLocaleString()} total
          </p>
        </div>
        <div className={`p-3 rounded-lg ${colorVariants[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  )
}

function MetricRow({
  label,
  value,
  percentage,
}: {
  label: string
  value: number
  percentage?: number
}) {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <span className="text-sm text-gray-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">{value.toLocaleString()}</span>
        {percentage !== undefined && (
          <span className="text-xs text-gray-500">({percentage}%)</span>
        )}
      </div>
    </div>
  )
}
